// ═══════════════════════════════════════════════════════════════
// ModuleNav — Responsive module sub-navigation
// Desktop: button bar. Mobile: hamburger menu dropdown.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Menu, Text, Divider } from '@mantine/core';
import { IconMenu2 } from '@tabler/icons-react';
import { brand } from '../../lib/colors';
import type { NavItem } from '../../types/nav';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface Props {
  items: NavItem[];
  groups?: NavGroup[];
  accentColor?: string;
  extra?: React.ReactNode; // e.g., "← Programs" button
}

export default function ModuleNav({ items, groups, accentColor, extra }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const color = accentColor || brand.blurple;

  // Find the active item label for the hamburger display
  const activeItem = items.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  // If groups provided, render grouped; otherwise flat list
  const renderDesktop = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => (
        <Group key={gi} gap={4} style={{ alignItems: 'center' }}>
          {gi > 0 && <Divider orientation="vertical" style={{ height: 20, margin: '0 4px' }} />}
          {group.label && <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ marginRight: 2 }}>{group.label}</Text>}
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? 'filled' : 'light'}
                color={active ? color : 'gray'}
                size="xs"
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            );
          })}
        </Group>
      ));
    }
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <Button
          key={item.path}
          variant={active ? 'filled' : 'light'}
          color={active ? color : 'gray'}
          size="xs"
          leftSection={<item.icon size={14} />}
          onClick={() => navigate(item.path)}
        >
          {item.label}
        </Button>
      );
    });
  };

  const renderMenuItems = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && <Menu.Divider />}
          {group.label && <Menu.Label>{group.label}</Menu.Label>}
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <Menu.Item
                key={item.path}
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
                style={active ? { backgroundColor: 'var(--mantine-color-blue-light)', fontWeight: 600 } : undefined}
              >
                {item.label}
              </Menu.Item>
            );
          })}
        </div>
      ));
    }
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <Menu.Item
          key={item.path}
          leftSection={<item.icon size={14} />}
          onClick={() => navigate(item.path)}
          style={active ? { backgroundColor: 'var(--mantine-color-blue-light)', fontWeight: 600 } : undefined}
        >
          {item.label}
        </Menu.Item>
      );
    });
  };

  return (
    <>
      {/* Desktop: button bar */}
      <Group gap={6} mb="md" className="module-nav-desktop"
        style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {extra}
        {renderDesktop()}
      </Group>

      {/* Mobile: hamburger menu */}
      <Group gap="sm" mb="md" className="module-nav-mobile"
        style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 8, alignItems: 'center' }}>
        {extra}
        <Menu shadow="lg" width={250} position="bottom-start" withArrow>
          <Menu.Target>
            <Button variant="light" color="gray" size="sm" leftSection={<IconMenu2 size={16} />}>
              {activeItem?.label || 'Navigate'}
            </Button>
          </Menu.Target>
          <Menu.Dropdown style={{ maxHeight: 400, overflowY: 'auto' }}>
            {renderMenuItems()}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </>
  );
}
